import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';

import AutoComplete from 'components/field/AutoComplete';
import Badge from 'components/field/Badge';
import IntegerInput from 'components/field/IntegerInput';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import * as S from './SavingsCalculator.styles';

const Calculator = ({ setCalculationData, setBillingPeriod }) => {
	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();
	const isSubmitting = useRef(false);

	const schema = useMemo(
		() =>
			yup.object().shape({
				billingMethod: yup.string().required(),
				numberOfShipments: yup.number().min(0).required(),
				deliveryCompanySettingsIds: yup.array().of(yup.number()).max(3).required()
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			billingMethod: 'monthly',
			numberOfShipments: 0,
			deliveryCompanySettingsIds: null
		}
	});

	const { handleSubmit, watch, setValue } = formMethods;

	const onSubmit = useCallback(
		(data) => {
			axiosPrivate
				.post(PRICE_CONFIGURATIONS.GET_SAVINGS_CALCULATOR_RESULT, data)
				.then((response) => {
					if (response.status === 200) {
						setCalculationData(response.data);
					} else {
						AppLogger.error(response);
					}
				})
				.catch((error) => {
					AppLogger.error(error);
					showError({ message: error.response?.data?.code });
				});
		},
		[axiosPrivate, setCalculationData, showError]
	);

	useEffect(() => {
		const subscription = watch((values) => {
			const isFormComplete = Object.values(values).every(
				(value) => !!value && (Array.isArray(value) ? value.length > 0 : true)
			);
			if (isFormComplete && !isSubmitting.current) {
				isSubmitting.current = true;
				handleSubmit(onSubmit)().finally(() => {
					isSubmitting.current = false;
				});
			} else {
				setCalculationData(null);
			}
		});
		return () => subscription.unsubscribe();
	}, [watch, handleSubmit, onSubmit, setCalculationData]);

	const fetchAutocompleteData = useCallback(
		async (props) => {
			try {
				const data = await axiosPrivate
					.post(PRICE_CONFIGURATIONS.GET_SAVINGS_CALCULATOR_DC_LIST)
					.then((res) => res.data);
				return data.filter((item) =>
					props?.keyword
						? item.name.toLowerCase().includes(props.keyword.toLowerCase())
						: props?.IDs
							? props.IDs.includes(item.ID)
							: true
				);
			} catch (error) {
				showError();
				AppLogger.error(error);
			}
		},
		[axiosPrivate, showError]
	);

	return (
		<S.CalculationContainer>
			<S.CardHeader>
				<Typography variant="p2" fontWeight="700">
					calculatorSettings
				</Typography>
				<Typography variant="p3" color="var(--content-3)">
					calculatorSettingsSubtitle
				</Typography>
			</S.CardHeader>

			<S.BorderlessForm formMethods={formMethods}>
				<FormItem label="billingPeriod">
					<S.PriceSwitch>
						<Typography variant="p3" fontWeight="500">
							monthly
						</Typography>
						<SwitchInput
							value={watch('billingMethod') === 'yearly'}
							setValue={() => {
								const newValue = watch('billingMethod') === 'monthly' ? 'yearly' : 'monthly';
								setValue('billingMethod', newValue);
								setBillingPeriod(newValue);
							}}
						/>
						<Typography variant="p3" fontWeight="500">
							yearly
						</Typography>
						<Badge variant="pill" size="small" color="green" text="otoModule.discount" />
					</S.PriceSwitch>
				</FormItem>

				<FormItem label="monthlyShipmentCount" required>
					<IntegerInput name="numberOfShipments" showArrows={false} unit="shipments" />
				</FormItem>

				{watch('billingMethod') === 'yearly' && watch('numberOfShipments') > 0 && (
					<Alert
						variant="info"
						showIcon
						className="my-3"
						content={
							<Trans
								i18nKey="shipmentsPerMonthAndPerYearAlert"
								values={{
									shipmentsPerMonth: watch('numberOfShipments'),
									shipmentsPerYear: watch('numberOfShipments') * 12
								}}
							/>
						}
					/>
				)}

				<FormItem label="selectDC" required>
					<AutoComplete
						fetchFunction={fetchAutocompleteData}
						value={watch('deliveryCompanySettingsIds')}
						name="deliveryCompanySettingsIds"
						selectionLabelAccessor={['name']}
						columns={[{ accessor: 'name', width: 1000 }]}
						setValue={(value) =>
							setValue(
								'deliveryCompanySettingsIds',
								value?.map((item) => item?.ID),
								{ shouldValidate: true, shouldDirty: true }
							)
						}
						multipleSelection
					/>
				</FormItem>

				{watch('deliveryCompanySettingsIds')?.length > 1 && watch('numberOfShipments') > 0 && (
					<Alert
						variant="info"
						showIcon
						className="my-3"
						content={
							<Trans
								i18nKey="eachDCHandlesNShipments"
								values={{
									shipments:
										watch('billingMethod') === 'yearly'
											? Math.round(
													(watch('numberOfShipments') * 12) /
														watch('deliveryCompanySettingsIds')?.length
												)
											: Math.round(
													watch('numberOfShipments') /
														watch('deliveryCompanySettingsIds')?.length
												)
								}}
							/>
						}
					/>
				)}
			</S.BorderlessForm>
		</S.CalculationContainer>
	);
};

export default Calculator;
